/**
 * Words for the two result lines: "Surat Baptis · Yohanes Yufuwai · 3 Sep 2026 · Paroki
 * Katedral Salib Suci Agats · Buku II/47/812" and "Baptis · Paroki St. Petrus Ewer ·
 * Buku I/12/45 · Jun 1987 · RP …". Dates follow the DNA: short month in a row, long month
 * in a sentence, partial dates as stored. The parish is named in full, "Paroki" before a name
 * stored without it (owner, 2026-10-04), as the letter it checks names it.
 */
import { formatPartialDateId } from '@satu/shared/dates'
import { namaParokiLengkap } from '@satu/shared/namaParoki'
import { isi, TEKS } from './strings'
import type { SearchHit, VerifyCodeResult } from './types'

/** ui.enum.jenisSurat, spelled here so the public page needs no registry. */
const JENIS_SURAT: Record<string, string> = {
  surat_baptis: 'Surat Baptis',
  surat_komuni: 'Surat Komuni Pertama',
  surat_krisma: 'Surat Krisma',
  surat_perkawinan: 'Surat Perkawinan',
  surat_kematian: 'Surat Kematian',
  surat_pindah: 'Surat Pindah',
  surat_pengantar_nikah: 'Surat Pengantar Nikah',
  kartu_keluarga: 'Kartu Keluarga Katolik',
}

const REGISTER: Record<string, string> = {
  baptis: 'Baptis',
  komuni_pertama: 'Komuni Pertama',
  krisma: 'Krisma',
  perkawinan: 'Perkawinan',
  kematian: 'Kematian',
}

export function labelJenisSurat(jenis: string): string {
  return JENIS_SURAT[jenis] ?? jenis
}

export function labelRegister(tabel: string): string {
  return REGISTER[tabel] ?? tabel
}

export function tanggalPendek(tanggal: string | null | undefined): string | null {
  return tanggal ? formatPartialDateId(tanggal, 'pendek') : null
}

export function tanggalPanjang(tanggal: string | null | undefined): string | null {
  return tanggal ? formatPartialDateId(tanggal, 'panjang') : null
}

/** "Buku II/47/812"; a missing page gives "Buku II/812". */
export function referensiBuku(ref: {
  buku: string | null
  halaman: number | null
  nomor: number | null
}): string | null {
  const parts = [ref.buku, ref.halaman, ref.nomor].filter(
    (part): part is string | number => part !== null && part !== '',
  )
  return parts.length === 0 ? null : `Buku ${parts.join('/')}`
}

/** One result row: a lead word, the person, then the details in order. */
export interface Baris {
  judul: string
  nama: string | null
  rincian: string[]
  /** A state sentence under the row (a replaced letter, a reissue). */
  keterangan: string | null
}

/** The parish as a letter names it; null without a name. */
function paroki(nama: string | null): string | null {
  return nama === null || nama.trim() === '' ? null : namaParokiLengkap(nama)
}

export function barisKode(hasil: VerifyCodeResult): Baris {
  const rincian = [
    tanggalPendek(hasil.surat.tanggal),
    paroki(hasil.paroki.nama),
    hasil.referensi === null ? null : referensiBuku(hasil.referensi),
  ].filter((part): part is string => part !== null)
  let keterangan: string | null = null
  if (hasil.status === 'digantikan') {
    keterangan = isi(TEKS.kode.digantikan, {
      tanggal: tanggalPanjang(hasil.digantikan_oleh?.tanggal) ?? '…',
    })
  } else if (hasil.menggantikan !== null) {
    keterangan = isi(TEKS.kode.terbitanUlang, {
      tanggal: tanggalPanjang(hasil.menggantikan.tanggal) ?? '…',
    })
  }
  return { judul: labelJenisSurat(hasil.surat.jenis), nama: hasil.nama, rincian, keterangan }
}

/** One fact of the public card: its label and its lines (a marriage letter names two). */
export interface FaktaKartu {
  label: string
  nilai: string[]
  /** A sentence in place of a name ("Belum tercatat" beside Kepala keluarga), read in muted ink. */
  redup: boolean
}

/** The public card of a letter found (release 15): its type, its state, three facts. */
export interface KartuSurat {
  judul: string
  status: 'asli' | 'digantikan'
  kalimat: string
  /** Under ASLI, for a reissue: "Terbitan ulang; menggantikan surat tanggal …". */
  terbitanUlang: string | null
  fakta: FaktaKartu[]
}

const KOSONG = '—'

function fakta(label: string, nilai: string | null, redup = false): FaktaKartu {
  return { label, nilai: [nilai ?? KOSONG], redup }
}

/**
 * The person the card names, labelled as the letter names them: the head of a family's letter
 * (never its KK number), both spouses of a marriage letter, the person of every other one.
 */
function faktaNama(hasil: VerifyCodeResult): FaktaKartu {
  const k = TEKS.periksa.kartu
  if (hasil.surat.jenis === 'kartu_keluarga' || (hasil.keluarga ?? null) !== null) {
    const kepala = hasil.keluarga?.kepala ?? null
    return kepala === null
      ? fakta(k.kepalaKeluarga, k.kepalaBelumTercatat, true)
      : fakta(k.kepalaKeluarga, kepala)
  }
  if (hasil.surat.jenis === 'surat_perkawinan') {
    const nama = [hasil.mempelai?.pria, hasil.mempelai?.wanita].filter(
      (n): n is string => typeof n === 'string' && n.trim() !== '',
    )
    return nama.length > 0
      ? { label: k.mempelai, nilai: nama, redup: false }
      : fakta(k.mempelai, hasil.nama)
  }
  return fakta(k.nama, hasil.nama)
}

/**
 * The public page's answer: never the book reference, the parish in full and never doubled,
 * the long date; a replaced letter says so with the reissue's date and keeps its facts.
 */
export function kartuKode(hasil: VerifyCodeResult): KartuSurat {
  const k = TEKS.periksa.kartu
  const digantikan = hasil.status === 'digantikan'
  return {
    judul: labelJenisSurat(hasil.surat.jenis),
    status: digantikan ? 'digantikan' : 'asli',
    kalimat: digantikan
      ? isi(k.digantikanKalimat, {
          tanggal: tanggalPanjang(hasil.digantikan_oleh?.tanggal) ?? '…',
        })
      : k.asliKalimat,
    terbitanUlang:
      !digantikan && hasil.menggantikan !== null
        ? isi(TEKS.kode.terbitanUlang, {
            tanggal: tanggalPanjang(hasil.menggantikan.tanggal) ?? '…',
          })
        : null,
    fakta: [
      faktaNama(hasil),
      fakta(k.diterbitkanOleh, paroki(hasil.paroki.nama)),
      fakta(k.tanggalTerbit, tanggalPanjang(hasil.surat.tanggal)),
    ],
  }
}

export function barisCatatan(hit: SearchHit): Baris {
  const rincian = [
    paroki(hit.paroki.nama),
    referensiBuku({ buku: hit.buku, halaman: hit.halaman, nomor: hit.nomor }),
    tanggalPendek(hit.tanggal),
    hit.pelayan,
  ].filter((part): part is string => part !== null)
  return { judul: labelRegister(hit.register), nama: hit.nama, rincian, keterangan: null }
}

/** The whole row as one sentence, for tests and assistive text. */
export function kalimatBaris(baris: Baris): string {
  return [baris.judul, baris.nama, ...baris.rincian]
    .filter((part): part is string => part !== null)
    .join(' · ')
}
